import type { BatMetadataConfigEntry } from './common/types';
import { coreFields, massField, massUnitField } from './common/commonFields';
import { vendorFields } from './common/vendorFields';
import { productFields, purchasedMaterialFields } from './common/productFields';
import { msg } from '@lingui/core/macro';
import {
  distanceQuantityField,
  distanceUnitField,
  transportationModeField,
} from './common/travelFields';

export const logisticsMetadata = {
  displayName: msg({
    message: 'Logistics',
    context: 'Activity data schema name for logistics',
  }),
  fields: {
    ...coreFields,
    ...vendorFields,
    ...productFields,
    ...purchasedMaterialFields,
    destination_location_city: {
      displayName: msg({
        message: 'Destination city',
        context: 'The display name for a dimension',
      }),
    },
    destination_location_country: {
      displayName: msg({
        message: 'Destination country',
        context: 'The display name for a dimension',
      }),
    },
    destination_location_postal_code: {
      displayName: msg({
        message: 'Destination postal code',
        context: 'The display name for a dimension',
      }),
    },
    destination_location_street_address: {
      displayName: msg({
        message: 'Destination street address',
        context: 'The display name for a dimension',
      }),
    },
    destination_location_street_address_2: {
      displayName: msg({
        message: 'Destination street address 2',
        context: 'The display name for a dimension',
      }),
    },
    destination_location_subdivision: {
      displayName: msg({
        message: 'Destination subdivision',
        context: 'The display name for a dimension',
      }),
    },
    distance_method: {
      displayName: msg({
        message: 'Distance method',
        context: 'The method by which the distance was calculated.',
      }),
      externalDescription: msg({
        message: 'The method by which the distance was calculated.',
        context: 'Distance method',
      }),
    },
    distance_quantity: distanceQuantityField,
    distance_unit: distanceUnitField,
    exclude_first_mile: {
      displayName: msg({
        message: 'Exclude first mile',
        context: `Excluded when the distance quantity given includes the 'first mile' the weight of goods are transported.`,
      }),
      externalDescription: msg({
        message: `Excluded when the distance quantity given includes the 'first mile' the weight of goods are transported.`,
        context: 'Exclude first mile',
      }),
    },
    exclude_last_mile: {
      displayName: msg({
        message: 'Exclude last mile',
        context: `Excluded when the distance quantity includes the 'last mile' the weight of goods are transported.`,
      }),
      externalDescription: msg({
        message: `Excluded when the distance quantity includes the 'last mile' the weight of goods are transported.`,
        context: 'Exclude last mile',
      }),
    },
    logistics_position: {
      displayName: msg({
        message: 'Logistics position',
        context: 'The display name for a dimension',
      }),
    },
    number_of_trips: {
      displayName: msg({
        message: 'Trip type',
        context:
          'Indicator of whether the trip is one-way (1) or round-trip (2)',
      }),
      externalDescription: msg({
        message:
          'Indicator of whether the trip is one-way (1) or round-trip (2)',
        context: 'Trip type',
      }),
    },
    origin_location_city: {
      displayName: msg({
        message: 'Origin city',
        context: 'The display name for a dimension',
      }),
    },
    origin_location_country: {
      displayName: msg({
        message: 'Origin country',
        context: 'The display name for a dimension',
      }),
    },
    origin_location_postal_code: {
      displayName: msg({
        message: 'Origin postal code',
        context: 'The display name for a dimension',
      }),
    },
    origin_location_street_address: {
      displayName: msg({
        message: 'Origin street address',
        context: 'The display name for a dimension',
      }),
    },
    origin_location_street_address_2: {
      displayName: msg({
        message: 'Origin street address 2',
        context: 'The display name for a dimension',
      }),
    },
    origin_location_subdivision: {
      displayName: msg({
        message: 'Origin subdivision',
        context: 'The display name for a dimension',
      }),
    },
    transportation_mode: transportationModeField,
    weight_quantity: massField,
    weight_unit: massUnitField,
  },
} as const satisfies BatMetadataConfigEntry;
